import moment from 'moment';
import { Button, Dropdown, Icon, InlineTooltip, Page, Tab, Table, Tabs, Tooltip } from '~/components';
import { TableBoxRowActions } from '~/components/table';
import { useApi, useIntegrations, useSubscription, useToast, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '~/styles';
import ClientMappings from './ClientMappings';
import ConfigurationDrawer from './ConfigurationDrawer';
import ExpenseCategoryMappings from './ExpenseCategoryMappings';
import InvoiceItemMappings from './InvoiceItemMappings';
import MemberMappings from './MemberMappings';
import PaymentMethodMappings from './PaymentMethodMappings';
import ProjectMappings from './ProjectMappings';
import SyncResults from './expenses/SyncResults';
import TaxRateMappings from './TaxRateMappings';

const Loading = () => <Icon icon="spinner" spin />;

const OK = () => <Icon icon="check" color={colors.success} />;

const Error = ({ message = "Something's not right. Try reconnecting to your QuickBooks company." }) => (
  <Tooltip message={message}>
    <Icon icon="exclamation-circle" color={colors.danger} />
  </Tooltip>
);

const Status = ({ status, message }) => {
  switch (status) {
    case 'ready':
      return <OK />;

    case 'error':
      return <Error message={message} />;

    default:
      return <Loading />;
  }
};

const Errors = styled.ul`
  font-size: 0.75rem;
  list-style: inside;

  li {
    list-style-type: disc;
  }
`;

const SyncResultsBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey10};
  border-radius: 0.3125rem;
`;

export default function QBOSettingsPage() {
  const { workspace } = useWorkspace();
  const api = useApi();
  const isMounted = useIsMounted();
  const toast = useToast();
  const location = useLocation();
  const { qboConnectionId } = useParams();

  const [company, setCompany] = useState({ data: null, status: 'loading', message: null });
  const [configuration, setConfiguration] = useState({ data: null, status: 'loading', message: null });
  const [integration, setIntegration] = useState({ data: null, status: 'loading', message: null });
  const [clients, setClients] = useState({ data: null, status: 'loading', message: null });
  const [invoiceItems, setInvoiceItems] = useState({ data: null, status: 'loading', message: null });
  const [paymentMethods, setPaymentMethods] = useState({ data: null, status: 'loading', message: null });
  const [taxRates, setTaxRates] = useState({ data: null, status: 'loading', message: null });

  const [memberMappings, setMemberMappings] = useState({ data: null, status: 'loading', message: null });
  const [expenseCategoryMappings, setExpenseCategoryMappings] = useState({
    data: null,
    status: 'loading',
    message: null,
  });

  const [tabIndex, setTabIndex] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [forceRefreshSyncsDate, setForceRefreshSyncsDate] = useState(moment().format());

  const setup = new URLSearchParams(location.search).get('action') === 'setup';

  const loadCompany = useCallback(async () => {
    setCompany((state) => ({ ...state, status: 'loading' }));

    try {
      const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getCompany();

      if (!isMounted.current) return;
      setCompany({ data, status: 'ready', message: null });
    } catch (error) {
      setCompany({ data: null, status: 'error', message: error.message });
    }
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const loadConfiguration = useCallback(async () => {
    setConfiguration((state) => ({ ...state, status: 'loading' }));

    try {
      const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getConfigurationStatus();

      if (!isMounted.current) return;
      if (data.code === 'ready') setConfiguration({ data, status: 'ready', message: null });
      else setConfiguration({ data, status: 'error', message: 'Review the configuration settings.' });
    } catch (error) {
      setConfiguration({ data: null, status: 'error', message: error.message });
    }
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const loadIntegration = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getIntegration();

    if (!isMounted.current) return;
    setIntegration({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const loadClients = useCallback(async () => {
    setClients((state) => ({ ...state, status: 'loading' }));

    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getClientMappings();

    if (!isMounted.current) return;
    setClients({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const loadInvoiceItems = useCallback(async () => {
    setInvoiceItems((state) => ({ ...state, status: 'loading' }));

    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getInvoiceItemMappings();

    if (!isMounted.current) return;
    setInvoiceItems({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const loadPaymentMethods = useCallback(async () => {
    setPaymentMethods((state) => ({ ...state, status: 'loading' }));

    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getPaymentMethodMappings();

    if (!isMounted.current) return;
    setPaymentMethods({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const loadTaxRates = useCallback(async () => {
    setTaxRates((state) => ({ ...state, status: 'loading' }));

    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getTaxRateMappings();

    if (!isMounted.current) return;
    setTaxRates({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const loadMemberMappings = useCallback(async () => {
    setMemberMappings((state) => ({ ...state, status: 'loading' }));

    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getMemberMappings();

    if (!isMounted.current) return;
    setMemberMappings({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const loadExpenseCategoryMappings = useCallback(async () => {
    setExpenseCategoryMappings((state) => ({ ...state, status: 'loading' }));

    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getExpenseCategoryMappings();

    if (!isMounted.current) return;
    setExpenseCategoryMappings({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const closeDialog = () => {
    setDialog(null);
  };

  const handleEditConfiguration = () => {
    setDialog('configuration');
  };

  const handleCloseConfiguration = async () => {
    await loadConfiguration();
    await loadMemberMappings();
    await loadExpenseCategoryMappings();
    closeDialog();
  };

  const handleEditClients = () => {
    setDialog('clients');
  };

  const handleCloseClients = async () => {
    await loadClients();
    closeDialog();
  };

  const setupConfiguration = useCallback(async () => {
    setConfiguration((state) => ({ ...state, status: 'loading' }));
    await api.www.workspaces(workspace.id).qbo(qboConnectionId).setupConfiguration();
    toast.success(`Loaded default configuration settings from QuickBooks.`);
    await loadConfiguration();
  }, [api, workspace.id, toast, loadConfiguration, qboConnectionId]);

  const setupClients = useCallback(async () => {
    setClients((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).setupClients();
    toast.success(`Loaded ${data.length} Customers from QuickBooks.`);
    await loadClients();
  }, [api, workspace.id, toast, loadClients, qboConnectionId]);

  const handleEditInvoiceItems = () => {
    setDialog('invoice_items');
  };

  const handleCloseInvoiceItems = async () => {
    await loadInvoiceItems();
    closeDialog();
  };

  const setupInvoiceItems = useCallback(async () => {
    setInvoiceItems((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).setupInvoiceItems();
    toast.success(`Loaded ${data.length} Products/Services from QuickBooks.`);
    await loadInvoiceItems();
  }, [api, workspace.id, toast, loadInvoiceItems, qboConnectionId]);

  const handleEditPaymentMethods = () => {
    setDialog('payment_methods');
  };

  const setupPaymentMethods = useCallback(async () => {
    setPaymentMethods((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).setupPaymentMethods();
    toast.success(`Loaded ${data.length} Payment Methods from QuickBooks.`);
    await loadPaymentMethods();
  }, [api, workspace.id, toast, loadPaymentMethods, qboConnectionId]);

  const handleClosePaymentMethods = async () => {
    await loadPaymentMethods();
    closeDialog();
  };

  const handleEditTaxRates = () => {
    setDialog('tax_rates');
  };

  const setupTaxRates = useCallback(async () => {
    setTaxRates((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).setupTaxRates();
    toast.success(`Loaded ${data.length} Tax Codes from QuickBooks.`);
    await loadTaxRates();
  }, [api, workspace.id, toast, loadTaxRates, qboConnectionId]);

  const handleCloseTaxRates = async () => {
    await loadTaxRates();
    closeDialog();
  };

  const handleEditMemberMappings = () => {
    setDialog('member_mappings');
  };

  const handleCloseMemberMappings = async () => {
    await loadMemberMappings();
    closeDialog();
  };

  const setupMemberMappings = useCallback(async () => {
    setMemberMappings((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).setupMemberMappings();
    toast.success(`Loaded ${data.length} Vendors from QuickBooks.`);
    await loadMemberMappings();
  }, [api, workspace.id, toast, loadMemberMappings, qboConnectionId]);

  const handleEditExpenseCategoryMappings = () => {
    setDialog('expense_category_mappings');
  };

  const handleCloseExpenseCategoryMappings = async () => {
    await loadExpenseCategoryMappings();
    closeDialog();
  };

  const setupExpenseCategoryMappings = useCallback(async () => {
    setExpenseCategoryMappings((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).setupExpenseCategoryMappings();
    toast.success(`Loaded ${data.length} Expense Accounts from QuickBooks.`);
    await loadExpenseCategoryMappings();
  }, [api, workspace.id, toast, loadExpenseCategoryMappings, qboConnectionId]);

  const handleSyncNow = useCallback(async () => {
    try {
      await api.www.workspaces(workspace.id).qbo(qboConnectionId).manualSync();
      toast.success('Reimbursable expenses sync has been queued.');
      setForceRefreshSyncsDate(moment().format());
    } catch (error) {
      toast.error(error.message);
    }
  }, [api, workspace.id, toast, qboConnectionId]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  useEffect(() => {
    if (setup) setupConfiguration();
    else if (company.status === 'ready') loadConfiguration();
  }, [loadConfiguration, setupConfiguration, setup, company.status]);

  useEffect(() => {
    loadIntegration();
  }, [loadIntegration]);

  const integrations = useIntegrations();
  const [QBOProjectsEnabled, setQBOProjectsEnabled] = useState(false);
  useEffect(() => {
    if (!integrations.qbo) return;
    if (company.status !== 'ready') return;

    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).preferences.projectsEnabled();
        setQBOProjectsEnabled(data);
      } catch {
        // Ignore
      }
    })();
  }, [workspace.id, integrations.qbo, api, qboConnectionId, company.status]);

  const [projects, setProjects] = useState({ data: null, status: 'loading', message: null });

  const loadProjects = useCallback(async () => {
    setProjects((state) => ({ ...state, status: 'loading' }));

    const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getProjectMappings();

    if (!isMounted.current) return;
    setProjects({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, qboConnectionId]);

  useEffect(() => {
    if (!QBOProjectsEnabled) return;
    loadProjects();
  }, [loadProjects, QBOProjectsEnabled]);

  const handleEditProjects = () => {
    setDialog('projects');
  };

  const handleCloseProjects = async () => {
    await loadProjects();
    closeDialog();
  };

  useEffect(() => {
    if (setup) setupClients();
    else loadClients();
  }, [loadClients, setupClients, setup]);

  useEffect(() => {
    if (setup) setupInvoiceItems();
    else loadInvoiceItems();
  }, [loadInvoiceItems, setupInvoiceItems, setup]);

  useEffect(() => {
    if (setup) setupPaymentMethods();
    else loadPaymentMethods();
  }, [loadPaymentMethods, setupPaymentMethods, setup]);

  useEffect(() => {
    if (setup) setupTaxRates();
    else loadTaxRates();
  }, [loadTaxRates, setupTaxRates, setup]);

  useEffect(() => {
    loadMemberMappings();
    loadExpenseCategoryMappings();
  }, [loadMemberMappings, loadExpenseCategoryMappings]);

  useEffect(() => {
    if (setup) window.history.pushState(null, null, window.location.href.split('?')[0]);
  }, [setup]);

  const { notify } = useSubscription();
  useEffect(() => {
    if (setup) {
      notify(useSubscription.keys.integrations_changed);
    }
  }, [setup, notify]);

  const disabled = company.status !== 'ready';

  return (
    <>
      <Page.Tabs>
        <Tabs selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
          <Tab>Settings</Tab>
          <Tab>Expense Sync</Tab>
        </Tabs>
      </Page.Tabs>

      {tabIndex === 0 && (
        <Page.Section>
          <Table>
            <Table.Header>
              <Table.Column width="12rem">Category</Table.Column>
              <Table.Column width="8rem" align="center">
                Status
              </Table.Column>
              <Table.Column>Details</Table.Column>
              <Table.BoxActionsColumn />
            </Table.Header>

            <Table.Body>
              <Table.BoxRow>
                <Table.Cell>
                  <strong>Company</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={company.status} message={company.message} />
                </Table.Cell>
                <Table.Cell>
                  {company.status === 'ready' && company.data.name}

                  {company.status === 'error' && integration.status === 'ready' && (
                    <a href={integration.data.authorizeUri}>Fix Connection</a>
                  )}
                </Table.Cell>

                <TableBoxRowActions>
                  <Button
                    disabled={integration.status !== 'ready'}
                    onClick={() => {
                      window.location = integration.data.authorizeUri;
                    }}>
                    <Icon icon="wrench" />
                    <InlineTooltip message="Reauthorize the QuickBooks connection." placement="left" />
                  </Button>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Configuration</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={configuration.status} message={configuration.message} />
                </Table.Cell>
                <Table.Cell>
                  {configuration.status !== 'loading' &&
                    (configuration.data?.code === 'ready' ? (
                      'Ready'
                    ) : (
                      <Errors>
                        {configuration.data?.errors?.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </Errors>
                    ))}
                </Table.Cell>

                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditConfiguration} />

                  <hr />

                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => {
                      return (
                        <>
                          <Dropdown.Item onClick={() => setIsOpen(false) || handleEditConfiguration()}>
                            Edit Mappings
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setIsOpen(false) || setupConfiguration()}>
                            Load from QuickBooks
                          </Dropdown.Item>
                        </>
                      );
                    }}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Clients</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={clients.status} message={clients.message} />
                </Table.Cell>
                <Table.Cell>
                  {clients.data && (
                    <>
                      {clients.data.filter((client) => !!client.qboCustomerId).length} of {clients.data.length}{' '}
                      connected
                    </>
                  )}
                </Table.Cell>

                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditClients} />

                  <hr />

                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => {
                      return (
                        <>
                          <Dropdown.Item onClick={() => setIsOpen(false) || handleEditClients()}>
                            Edit Mappings
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setIsOpen(false) || setupClients()}>
                            Load from QuickBooks
                          </Dropdown.Item>
                        </>
                      );
                    }}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Invoice Items</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={invoiceItems.status} message={invoiceItems.message} />
                </Table.Cell>
                <Table.Cell>
                  {invoiceItems.data && (
                    <>
                      {invoiceItems.data.filter((invoiceItem) => !!invoiceItem.qboItemId).length} of{' '}
                      {invoiceItems.data.length} connected
                    </>
                  )}
                </Table.Cell>

                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditInvoiceItems} />

                  <hr />

                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => {
                      return (
                        <>
                          <Dropdown.Item onClick={() => setIsOpen(false) || handleEditInvoiceItems()}>
                            Edit Mappings
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setIsOpen(false) || setupInvoiceItems()}>
                            Load from QuickBooks
                          </Dropdown.Item>
                        </>
                      );
                    }}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Payment Methods</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={paymentMethods.status} message={paymentMethods.message} />
                </Table.Cell>
                <Table.Cell>
                  {paymentMethods.data && (
                    <>
                      {paymentMethods.data.filter((paymentMethod) => !!paymentMethod.qboPaymentMethodId).length} of{' '}
                      {paymentMethods.data.length} connected
                    </>
                  )}
                </Table.Cell>

                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditPaymentMethods} />

                  <hr />

                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => {
                      return (
                        <>
                          <Dropdown.Item onClick={() => setIsOpen(false) || handleEditPaymentMethods()}>
                            Edit Mappings
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setIsOpen(false) || setupPaymentMethods()}>
                            Load from QuickBooks
                          </Dropdown.Item>
                        </>
                      );
                    }}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Tax Rates</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={taxRates.status} message={taxRates.message} />
                </Table.Cell>
                <Table.Cell>
                  {taxRates.data && (
                    <>
                      {taxRates.data.filter((taxRate) => !!taxRate.qboTaxCodeId).length} of {taxRates.data.length}{' '}
                      connected
                    </>
                  )}
                </Table.Cell>

                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditTaxRates} />

                  <hr />

                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => {
                      return (
                        <>
                          <Dropdown.Item onClick={() => setIsOpen(false) || handleEditTaxRates()}>
                            Edit Mappings
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setIsOpen(false) || setupTaxRates()}>
                            Load from QuickBooks
                          </Dropdown.Item>
                        </>
                      );
                    }}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              {QBOProjectsEnabled && (
                <Table.BoxRow>
                  <Table.Cell>
                    <strong>Projects</strong>
                  </Table.Cell>
                  <Table.Cell>
                    <Status status={projects.status} message={projects.message} />
                  </Table.Cell>
                  <Table.Cell>
                    {projects.data && (
                      <>
                        {projects.data.filter((project) => !!project.qboProjectId).length} of {projects.data.length}{' '}
                        connected
                      </>
                    )}
                  </Table.Cell>

                  <TableBoxRowActions>
                    <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditProjects} />

                    <hr />

                    <TableBoxRowActions.Dropdown disabled={disabled}>
                      {({ setIsOpen }) => {
                        return (
                          <>
                            <Dropdown.Item onClick={() => setIsOpen(false) || handleEditProjects()}>
                              Edit Mappings
                            </Dropdown.Item>
                          </>
                        );
                      }}
                    </TableBoxRowActions.Dropdown>
                  </TableBoxRowActions>
                </Table.BoxRow>
              )}

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Members</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={memberMappings.status} message={memberMappings.message} />
                </Table.Cell>
                <Table.Cell>
                  {memberMappings.data && (
                    <>
                      {memberMappings.data.filter((m) => !!m.qboVendorId).length} of {memberMappings.data.length}{' '}
                      connected
                    </>
                  )}
                </Table.Cell>

                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditMemberMappings} />

                  <hr />

                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => {
                      return (
                        <>
                          <Dropdown.Item onClick={() => setIsOpen(false) || handleEditMemberMappings()}>
                            Edit Mappings
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setIsOpen(false) || setupMemberMappings()}>
                            Load from QuickBooks
                          </Dropdown.Item>
                        </>
                      );
                    }}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Expense Categories</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={expenseCategoryMappings.status} message={expenseCategoryMappings.message} />
                </Table.Cell>
                <Table.Cell>
                  {expenseCategoryMappings.data && (
                    <>
                      {expenseCategoryMappings.data.filter((c) => !!c.qboAccountId).length} of{' '}
                      {expenseCategoryMappings.data.length} connected
                    </>
                  )}
                </Table.Cell>

                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditExpenseCategoryMappings} />

                  <hr />

                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => {
                      return (
                        <>
                          <Dropdown.Item onClick={() => setIsOpen(false) || handleEditExpenseCategoryMappings()}>
                            Edit Mappings
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setIsOpen(false) || setupExpenseCategoryMappings()}>
                            Load from QuickBooks
                          </Dropdown.Item>
                        </>
                      );
                    }}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>
            </Table.Body>
          </Table>
        </Page.Section>
      )}

      {tabIndex === 1 && (
        <Page.Section>
          <SyncResultsBlock>
            <SyncResults refreshDate={forceRefreshSyncsDate} onSyncNow={handleSyncNow} />
          </SyncResultsBlock>
        </Page.Section>
      )}

      {dialog &&
        {
          configuration: () => <ConfigurationDrawer onClose={handleCloseConfiguration} />,
          clients: () => <ClientMappings onClose={handleCloseClients} />,
          invoice_items: () => <InvoiceItemMappings onClose={handleCloseInvoiceItems} />,
          payment_methods: () => <PaymentMethodMappings onClose={handleClosePaymentMethods} />,
          tax_rates: () => <TaxRateMappings onClose={handleCloseTaxRates} />,
          projects: () => <ProjectMappings onClose={handleCloseProjects} />,
          member_mappings: () => <MemberMappings onClose={handleCloseMemberMappings} />,
          expense_category_mappings: () => <ExpenseCategoryMappings onClose={handleCloseExpenseCategoryMappings} />,
        }[dialog]()}
    </>
  );
}
